/**
 * Analytics tracking helper — RW-23.
 *
 * Two layers:
 *   1. Firebase Analytics (GA4) — fire-and-forget logEvent call. Powers the
 *      Google Analytics dashboard. No PII beyond what we explicitly pass.
 *   2. Existing Firestore queue (`src/lib/analytics.js`) — buffered, debounced
 *      writes to the `analytics` collection for our internal admin dashboards.
 *      Auto-enriches with userId / displayName / role / office / sessionId
 *      from the user passed to `initAnalytics()` in App.js.
 *
 * Both layers MUST never throw — analytics is fire-and-forget and is wrapped
 * in try/catch at every boundary so a missing measurementId or a network
 * blip can never crash the app.
 *
 * For events that fire BEFORE `initAnalytics()` is called (notably the
 * LOGIN_SUCCESS / LOGIN_FAILED events at the auth boundary), use
 * `trackWithUser()` which writes directly to Firestore with an explicit user
 * object instead of relying on the buffered queue.
 */

import { addDoc, collection, serverTimestamp } from 'firebase/firestore';
import { db, logAnalyticsEvent } from '../firebase';
import { withTenant } from '../services/data';
import { trackEvent } from '../lib/analytics';
import { EVENTS, MODULES } from './events';

// Re-export so call sites can do `import { track, EVENTS } from '../analytics/track'`
export { EVENTS, MODULES };

/**
 * Standard tracker — call this from anywhere inside the authenticated app.
 *
 * @param {string} eventName  one of EVENTS.*
 * @param {object} [properties]  per-event properties (e.g. { moduleName, listingId })
 */
export function track(eventName, properties = {}) {
  /* RW-2313: BOTH layers are inside the guard. Layer 1 used to sit outside it,
     which made the promise at the top of this file false — and the failure mode
     is nastier than a lost event, because most track() calls live in the try
     block of a business action. A throwing tracker there is caught by THAT
     catch, so a create that succeeded reports as a failure and the record the
     server already wrote goes unmentioned. (It surfaced as `logAnalyticsEvent
     is not a function` under a suite that mocks ../firebase — the same shape a
     half-initialised SDK produces at runtime.) */
  try {
    // Layer 1: Firebase Analytics (GA4)
    logAnalyticsEvent(eventName, properties);
  } catch (e) {
    if (typeof console !== 'undefined') console.warn('track() GA layer failed:', e?.message);
  }
  try {
    // Layer 2: internal Firestore queue (auto-enriched, debounced, batched)
    trackEvent(eventName, properties);
  } catch (e) {
    // Analytics must never crash the app.
    if (typeof console !== 'undefined') console.warn('track() failed:', e?.message);
  }
}

/**
 * Tracker variant for events that fire BEFORE `initAnalytics(user)` has run
 * — i.e. the auth boundary (LOGIN_SUCCESS / LOGIN_FAILED). Writes directly
 * to Firestore with an explicit user object so the event is not silently
 * dropped by the queue's user-context guard.
 *
 * @param {string} eventName
 * @param {object|null} user  Firebase auth user (or null for failed login)
 * @param {object} [properties]
 */
export function trackWithUser(eventName, user, properties = {}) {
  // Layer 1: Firebase Analytics — same as track(), same RW-2313 guard.
  try {
    logAnalyticsEvent(eventName, properties);
  } catch (e) {
    if (typeof console !== 'undefined') console.warn('trackWithUser() GA layer failed:', e?.message);
  }

  // Layer 2: direct Firestore write (bypasses the buffered queue, which
  // requires initAnalytics() to have already been called).
  try {
    addDoc(collection(db, 'analytics'), withTenant('analytics', {
      event: eventName,
      userId: user?.uid || 'anonymous',
      email: user?.email || '',
      displayName: user?.displayName || '',
      properties: properties || {},
      page: typeof window !== 'undefined' ? window.location.pathname : '',
      // RW-2379: this path runs at the AUTH boundary, before the users/{uid}
      // doc (and therefore roles[]/accessLevel) has loaded — privilege is
      // genuinely unknowable here. null means "unknown", and a reader must
      // resolve it by joining userId to users/{uid}. Writing false instead
      // would assert this was agent traffic, which is exactly the error
      // RW-2379 exists to stop. This path is ~0.7% of all events.
      isInternal: null,
      accessLevel: '',
      timestamp: serverTimestamp(),
    })).catch(() => {
      // Silent fail — analytics is never load-bearing.
    });
  } catch (e) {
    if (typeof console !== 'undefined') console.warn('trackWithUser() failed:', e?.message);
  }
}
